import {
  MapViewerFlagsAPI,
  MapViewerLoadMapOptions,
  MapViewerLoadObject,
  MapViewerNode,
  MapViewerPluginsList,
  MapViewerStyles,
  MapViewerStylesState,
  Viewer3dLoadView3dOptions,
} from '@3ddv/dvm-internal';
import {
  MapViewerInitOptions,
  MapViewerService,
  Viewer3dInitOptions,
  Viewer3dService,
} from '@3ddv/ngx-dvm-internal';
import {
  Injectable,
  OnDestroy,
  Signal,
  ViewContainerRef,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Placement, createPopper } from '@popperjs/core';
import {
  Observable,
  Subscription,
  firstValueFrom,
  from,
  tap,
  timer,
} from 'rxjs';
import { DvmData, DvmStyles } from '../models/configuration.model';
import { BG_MAP_VIEWER_TOKEN } from '../tokens/bg-map-viewer.token';
import { MINIMAP_TOKEN } from '../tokens/minimap.token';
import { ConfigurationService } from './configuration.service';
import { DemoDataService } from './demo-data.service';
import { UnavailableSectionsStore } from './unavailable-sections.store';
import { UtilitiesService } from './utilities.service';

const SECTION_AVAILABILITY_TYPES = ['section', 'general_admission'];

@Injectable({
  providedIn: 'root',
})
export class DvmService implements OnDestroy {
  private utilitiesService = inject(UtilitiesService);
  public readonly viewerService = inject(MapViewerService);
  public readonly configurationService = inject(ConfigurationService);
  public readonly minimapService = inject(MINIMAP_TOKEN);
  public readonly viewer3dService = inject(Viewer3dService);
  private readonly bgViewerService = inject(BG_MAP_VIEWER_TOKEN);
  private relatedMapsQueue: Promise<void> = Promise.resolve();
  private readonly relatedMapsCache = new Map<
    string,
    Record<string, { map_id: string }> | null
  >();

  private demoDataService = inject(DemoDataService);
  private readonly unavailableSections = inject(UnavailableSectionsStore);

  private mapViewerInitOptions: MapViewerInitOptions = { plugins: [] };
  private viewer3dInitOptions: Viewer3dInitOptions = {};

  public mapViewerLoadOptions: MapViewerLoadMapOptions = {
    venue_id: '',
    map_id: '',
  };
  public minimapLoadOptions: MapViewerLoadMapOptions = {
    venue_id: '',
    map_id: '',
  };
  private viewer3dLoadOptions: Viewer3dLoadView3dOptions = {
    venue_id: '',
    view_id: '',
  };

  private customStyles!: DvmStyles;

  private readonly baseAvailability: string[] = [];
  isFirstSeatMapLoad = true;
  isMultiSelectTooltipVisible: boolean = false;
  popoverPlacement: ViewContainerRef;
  viewerSubscriptions: Subscription[] = [];
  private sectionsMmcIdToTdc: Record<string, any> = {};
  private sectionsTdcToMmc: Record<string, any> = {};

  private _isTopView: WritableSignal<boolean> = signal(true);
  private _is3dView: WritableSignal<boolean> = signal(false);
  private _showMinimap: WritableSignal<boolean> = signal(false);
  public readonly isTopView: Signal<boolean> = computed(() =>
    this._isTopView()
  );
  public readonly is3dView: Signal<boolean> = computed(() => this._is3dView());
  public readonly showMinimap: Signal<boolean> = computed(() =>
    this._showMinimap()
      ? !this.is3dView() && !this.isTopView()
      : this._showMinimap()
  );

  public createPopover(
    node: MapViewerNode,
    tooltip: HTMLElement,
    options: Parameters<typeof createPopper>[2]
  ) {
    const popper = createPopper(node, tooltip, options);

    tooltip.setAttribute('data-show', '');

    return popper;
  }

  public getPlacement(node: MapViewerNode, elementDOMId: string): Placement {
    const nodeDomCenter = this.viewerService.fromSceneToDom([
      node.aabb![0],
      node.aabb![1],
    ]);
    const nodeHalfHeight = node.aabb![3] / 2;
    const nodeVerticalCenter = nodeDomCenter![1] + nodeHalfHeight;
    const element = document.getElementById(elementDOMId) as HTMLElement;
    return nodeVerticalCenter > element.offsetHeight / 2 ? 'top' : 'bottom';
  }

  public getOffset(node: MapViewerNode): [number, number] {
    return node.type === 'seat' ? [0, 6] : [0, -10];
  }

  private formatRgb: (rgbString: string) => string =
    this.utilitiesService.formatRgb;

  public initMapViewer(
    data: DvmData,
    plugins?: MapViewerPluginsList[] | null,
    flags?: MapViewerFlagsAPI | null,
    customStyles?: DvmStyles | null,
    customClient?: string | null,
    baseAvailability?: string[] | null,
    debugMode?: boolean
  ): void {
    if (!this.viewerService.isInitialized()) {
      plugins ? this.setPlugins(plugins) : false;

      customStyles ? (this.customStyles = customStyles) : false;

      customClient ? this.setClient(customClient) : this.setClient();

      const subscription = this.viewerService
        .initialize(this.mapViewerInitOptions)
        .subscribe(() => {
          this.setViewerLoadOptions(data);

          if (baseAvailability) {
            this.setBaseAvailability(baseAvailability);
          }

          const subscription = this.loadMap(
            this.mapViewerLoadOptions
          ).subscribe(() => {
            if (flags) {
              this.setFlags(flags);
            }

            if (baseAvailability && !this.demoDataService.isDemoActive()) {
              this.setMapAvailability(undefined, baseAvailability);
            }

            if (debugMode) {
              (window as any)['viewer'] = this.viewerService;
            }
          });

          this.viewerSubscriptions.push(subscription);
        });

      this.viewerSubscriptions.push(subscription);
    } else {
      this.setViewerLoadOptions(data);

      if (baseAvailability) {
        this.setBaseAvailability(baseAvailability);
      }

      const subscription = this.loadMap(this.mapViewerLoadOptions).subscribe(
        () => {
          if (flags) {
            this.setFlags(flags);
          }

          if (baseAvailability && !this.demoDataService.isDemoActive()) {
            this.setMapAvailability(undefined, this.baseAvailability);
          }

          if (debugMode) {
            (window as any)['viewer'] = this.viewerService;
          }
        }
      );

      this.viewerSubscriptions.push(subscription);
    }
  }

  public initViewer3d(venue_id?: string): void {
    if (!this.viewer3dService.isInitialized()) {
      const subscription = this.viewer3dService
        .initialize(this.viewer3dInitOptions)
        .subscribe(() => {
          this.setViewer3dLoadOptions(venue_id);
        });
      this.viewerSubscriptions.push(subscription);
    }
  }

  public initMinimap(dvmData: DvmData, debugMode?: boolean): void {
    if (!dvmData.miniMapId && !dvmData.miniMapEnabled) {
      console.log('No se ha configurado el minimapa');
      return;
    }

    this._showMinimap.update(x => !x);

    if (!this.minimapService.isInitialized()) {
      const options = Object.assign({}, this.mapViewerInitOptions);
      options.client_id = dvmData.clientId ?? 'tdc';

      const subscription = this.minimapService.initialize(options).subscribe({
        next: () => {
          if (debugMode) {
            (window as any)['minimap'] = this.minimapService;
          }

          this.setFlagsMini();
          this.setMinimapLoadOptions(dvmData);
          this.loadMinimap();
        },
        error: e => console.log(e),
      });
      this.viewerSubscriptions.push(subscription);
    }

    return;
  }

  public openSectionMap(): Observable<MapViewerLoadObject> {
    return this.loadMap();
  }

  public openSeatMap(sectionId: string): Observable<MapViewerLoadObject> {
    const loadOptions: MapViewerLoadMapOptions = this.getMapLoadOptions();
    loadOptions.map_id = sectionId;

    return this.loadMap(loadOptions);
  }

  public setMapAvailability(type?: string, availability?: string[]): void {
    if (type && availability) {
      this.viewerService.setAvailability(
        type,
        this.withoutUnavailableSections(type, availability)
      );
    } else {
      this.viewerService.getTypesList().forEach(t => {
        const availabilityType = type ?? t;
        const ids =
          availability ??
          this.viewerService
            .getNodesByType(availabilityType)
            .map(node => node.id);

        this.viewerService.setAvailability(
          availabilityType,
          this.withoutUnavailableSections(availabilityType, ids)
        );
      });
    }
  }

  private withoutUnavailableSections(
    type: string,
    sectionIds: string[]
  ): string[] {
    if (!SECTION_AVAILABILITY_TYPES.includes(type)) {
      return sectionIds;
    }

    return sectionIds.filter(
      sectionId => !this.unavailableSections.ids.has(sectionId)
    );
  }

  public getMapLoadOptions(): MapViewerLoadMapOptions {
    return Object.assign({}, this.mapViewerLoadOptions);
  }

  public getViewer3dLoadOptions(): Viewer3dLoadView3dOptions {
    return Object.assign({}, this.viewer3dLoadOptions);
  }

  public async initViewerBg(): Promise<void> {
    if (this.bgViewerService.isInitialized()) {
      return;
    }

    try {
      await firstValueFrom(
        this.bgViewerService.initialize({
          client_id: this.mapViewerInitOptions['client_id'],
          plugins: ['related'],
        })
      );
    } catch (error) {
      console.error('[DVM] background viewer init error', error);
    }
  }

  public getRelatedMapsForSection(
    sectionId: string
  ): Promise<Record<string, { map_id: string }> | null> {
    const loadOptions = this.getMapLoadOptions();
    const cacheKey = `${loadOptions.venue_id}:${sectionId}`;

    if (this.relatedMapsCache.has(cacheKey)) {
      return Promise.resolve(this.relatedMapsCache.get(cacheKey) ?? null);
    }

    const request = this.relatedMapsQueue.then(() =>
      this.loadRelatedMaps(sectionId, loadOptions, cacheKey)
    );
    this.relatedMapsQueue = request.then(
      () => undefined,
      () => undefined
    );
    return request;
  }

  private async loadRelatedMaps(
    sectionId: string,
    loadOptions: MapViewerLoadMapOptions,
    cacheKey: string
  ): Promise<Record<string, { map_id: string }> | null> {
    try {
      await this.initViewerBg();
      loadOptions.map_id = sectionId;
      await firstValueFrom(from(this.bgViewerService.loadMap(loadOptions)));
      const relatedMaps =
        this.bgViewerService.related?.getRelatedMaps() ?? null;
      this.relatedMapsCache.set(cacheKey, relatedMaps);
      return relatedMaps;
    } catch (error) {
      console.error('[DVM] related maps error', error);
      return null;
    }
  }

  public loadThumbnail(viewId: string): Observable<HTMLImageElement> {
    const loadOptions: Viewer3dLoadView3dOptions = {
      venue_id: this.mapViewerLoadOptions.venue_id,
      view_id: viewId,
    };

    return from(this.viewerService.getThumbnail(loadOptions));
  }

  public load3dView(viewId: string): void {
    if (this.viewer3dService.isLoaded()) {
      const viewLoaded = this.viewer3dService.getViewId();

      if (viewLoaded !== viewId) {
        this.viewer3dService.reset();
      }
    }

    const load3dOptions: Viewer3dLoadView3dOptions =
      this.getViewer3dLoadOptions();

    load3dOptions.view_id = viewId;

    const subscription = from(
      this.viewer3dService.loadView3d(load3dOptions)
    ).subscribe({
      next: () => {
        this.setFlags3d();

        !this.is3dView() ? this._is3dView.update(x => !x) : null;

        this.viewer3dService.interface.setPoweredByLabel({
          position: 'bottomright',
        });
      },
      error: (e: unknown) => console.log(e),
    });

    this.viewerSubscriptions.push(subscription);
  }

  public close3dView(): void {
    if (this.is3dView()) {
      this._is3dView.set(false);
    }
    return;
  }

  public translateSectionMmcId(mmcId: string): string | null {
    return this.sectionsMmcIdToTdc[mmcId] || null;
  }

  public translateSectionTdcId(tdcId: string): string | null {
    return this.sectionsTdcToMmc[tdcId] || null;
  }

  private setViewerLoadOptions(data: DvmData): void {
    this.mapViewerLoadOptions.venue_id = data.venueId;
    this.mapViewerLoadOptions.map_id = data.mapId;
  }

  private setMinimapLoadOptions(data: DvmData): void {
    this.minimapLoadOptions.venue_id = data.venueId;
    this.minimapLoadOptions.map_id = data.miniMapId;
  }

  private setViewer3dLoadOptions(venue_id?: string): void {
    this.viewer3dLoadOptions.venue_id =
      venue_id ?? this.viewerService.getVenueId()!;
  }

  public loadMap(
    loadOptions?: MapViewerLoadMapOptions
  ): Observable<MapViewerLoadObject> {
    const mapId = loadOptions
      ? (loadOptions.map_id as string)
      : (this.mapViewerLoadOptions.map_id as string);

    const isTopView = this.checkIsTopView(mapId as string);
    this.zoomInMinimap(isTopView, mapId as string);
    this.showMultiSelecTooltip(isTopView);

    if (this.showMinimap()) {
      this._showMinimap.update(x => !x);
    }

    return from(
      this.viewerService.loadMap(loadOptions ?? this.mapViewerLoadOptions)
    ).pipe(
      tap(() => this.setFlags()),
      tap(() => this.applyStyles()),
      tap(() => this.updateSectionIdTranslation()),
      tap(() => this._showMinimap.update(x => !x)),
      tap(() => {
        if (this.demoDataService.isDemoActive()) {
          this.demoDataService.setDemoAvailability(this.viewerService);
        }
      })
    );
  }

  private zoomInMinimap(isTopView: boolean, sectionMapId: string): void {
    if (isTopView) {
      if (
        this.minimapService.isLoaded() &&
        this.minimapService.isInitialized()
      ) {
        this.minimapService.goTo([0, 0], 1, 100);
      }
    } else {
      if (
        !this.minimapService.isLoaded() ||
        !this.minimapService.isInitialized()
      ) {
        return;
      }

      const tdcId = this.translateSectionTdcId(sectionMapId);
      const nodeId = tdcId ?? sectionMapId;
      const selectedSectionNodes = this.minimapService.getNodesByState(
        'section',
        'selected'
      );
      this.minimapService.setState(selectedSectionNodes, 'unavailable', true);

      const timeoutId = setTimeout(() => {
        this.minimapService.setState(nodeId, 'selected', true);
        this.minimapService.goTo(tdcId ?? sectionMapId, 3, 1500);
        clearTimeout(timeoutId);
      }, 3000);
    }
  }

  private showMultiSelecTooltip(isTopView: boolean): void {
    if (isTopView) {
      this.isMultiSelectTooltipVisible = false;
    } else {
      this.isMultiSelectTooltipVisible = !!this.isFirstSeatMapLoad;
      const isFirstSeatMapLoad = this.isFirstSeatMapLoad;
      const multiSelectionToastSubscription = timer(10000).subscribe(() => {
        if (isFirstSeatMapLoad) {
          this.isMultiSelectTooltipVisible = false;
        }
        multiSelectionToastSubscription.unsubscribe();
      });
      this.viewerSubscriptions.push(multiSelectionToastSubscription);
    }
  }

  private loadMinimap() {
    const subscription = from(
      this.minimapService.loadMap(this.minimapLoadOptions)
    ).subscribe({
      next: () => {
        this.setFlagsMini();
        this._showMinimap.update(x => !x);
      },
      error: (e: unknown) => console.log(e),
    });

    this.viewerSubscriptions.push(subscription);
  }

  private setClient(client: string = 'tdc'): void {
    this.mapViewerInitOptions['client_id'] = client;
  }

  private setPlugins(plugins: MapViewerPluginsList[]): void {
    this.mapViewerInitOptions['plugins'] = plugins;
  }

  private setFlags(flags?: MapViewerFlagsAPI): void {
    if (!flags) {
      if (this.configurationService.configuration.dvmData.limitedZoom) {
        this.viewerService.flags.max_zoom_on_first_limit = true;
      }

      this.viewerService.flags.preserve_min_scaling_factor = true;
      this.viewerService.flags.fixed_aspect_ratio = false;
      this.viewerService.flags.automatic_selection = false;
    } else {
      Object.entries(flags).forEach(([key, value]) => {
        if (this.viewerService.flags.hasOwnProperty(key)) {
          (this.viewerService.flags as unknown as Record<string, unknown>)[
            key
          ] = value;
        }
      });
    }
  }

  private setFlagsMini(flags?: MapViewerFlagsAPI): void {
    if (!flags) {
      this.minimapService.flags.fixed_aspect_ratio = false;
      this.minimapService.flags.automatic_selection = false;
      this.minimapService.flags.automatic_hover = false;
      this.minimapService.flags.zooming = false;
      this.minimapService.flags.panning = false;
    } else {
      Object.entries(flags).forEach(([key, value]) => {
        if (this.minimapService.flags.hasOwnProperty(key)) {
          (this.minimapService.flags as unknown as Record<string, unknown>)[
            key
          ] = value;
        }
      });
    }
  }

  private setFlags3d(): void {
    this.viewer3dService.flags.fixed_aspect_ratio = false;
  }

  private applyStyles(): void {
    if (!this.customStyles) {
      return;
    }

    const styles: MapViewerStyles = this.viewerService.getStyles();

    if (styles && styles[0]['section']) {
      const sectionAvailable: MapViewerStylesState = styles[0]['section'][
          'available'
        ]! as MapViewerStylesState,
        sectionSelected: MapViewerStylesState = styles[0]['section'][
          'selected'
        ]! as MapViewerStylesState;

      sectionAvailable.normal.none.fillStyle = this.formatRgb(
        this.customStyles['section-available-normal-fill']
      );
      sectionAvailable.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['section-available-normal-stroke']
      );

      sectionAvailable.hover!.none.fillStyle = this.formatRgb(
        this.customStyles['section-available-hover-fill']
      );
      sectionAvailable.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-available-hover-stroke']
      );

      sectionSelected.normal.none.fillStyle = this.formatRgb(
        this.customStyles['section-selected-normal-fill']
      );

      sectionSelected.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['section-selected-normal-stroke']
      );

      sectionSelected.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-selected-hover-stroke']
      );

      styles[0]['section']['available'] = sectionAvailable;
      styles[0]['section']['selected'] = sectionSelected;
    }

    if (styles && styles[0]['section_nh']) {
      const nhAvailable: MapViewerStylesState = styles[0]['section_nh'][
        'available'
      ]! as MapViewerStylesState;
      const nhUnavailable: MapViewerStylesState = styles[0]['section_nh'][
        'unavailable'
      ]! as MapViewerStylesState;

      const isTouchDevice =
        this.utilitiesService.isMobile || this.utilitiesService.isTablet;

      nhAvailable.hover!.none.fillStyle = this.formatRgb(
        this.customStyles['section-nh-available-hover-fill']
      );
      nhAvailable.hover!.none.fillOpacity = 0.3;
      nhAvailable.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-nh-available-hover-stroke']
      );
      nhAvailable.hover!.none.strokeOpacity = 1;

      if (isTouchDevice) {
        nhAvailable.normal.none = { ...nhAvailable.hover!.none };
      } else {
        nhAvailable.normal.none.fillStyle = 'transparent';
        nhAvailable.normal.none.strokeStyle = 'transparent';
      }

      nhUnavailable.normal.none.fillStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-normal-fill']
      );
      nhUnavailable.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-normal-stroke']
      );

      nhUnavailable.hover!.none.fillStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-hover-fill']
      );
      nhUnavailable.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-hover-stroke']
      );

      styles[0]['section_nh']['available'] = nhAvailable;
      styles[0]['section_nh']['unavailable'] = nhUnavailable;
    }

    if (styles && styles[0]['seat']) {
      const seatAvailable: MapViewerStylesState = styles[0]['seat'][
          'available'
        ]! as MapViewerStylesState,
        seatUnavailable: MapViewerStylesState = styles[0]['seat'][
          'unavailable'
        ]! as MapViewerStylesState;

      seatAvailable.normal.none.fillStyle = this.formatRgb(
        this.customStyles['seat-available-none-fill']
      );
      seatAvailable.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['seat-available-none-stroke']
      );
      seatAvailable.normal.none.icon = undefined;

      if (seatAvailable.hover && seatAvailable.hover.none) {
        seatAvailable.hover.none.icon = undefined;
      }

      seatAvailable.normal['disabled'].fillStyle = this.formatRgb(
        this.customStyles['seat-available-disabled-fill']
      );
      seatAvailable.normal['disabled'].strokeStyle = this.formatRgb(
        this.customStyles['seat-available-disabled-stroke']
      );

      seatAvailable.normal['pending'].fillStyle = this.formatRgb(
        this.customStyles['seat-available-pending']
      );
      seatAvailable.normal['pending'].strokeStyle = this.formatRgb(
        this.customStyles['seat-available-pending']
      );

      seatUnavailable.normal['none'] = {
        fillStyle: this.formatRgb(this.customStyles['seat-unavailable-none']),
        strokeStyle: this.formatRgb(this.customStyles['seat-unavailable-none']),
        lineWidth: 0.05,
        opacity: 0.5,
      };

      styles[0]['seat']['available'] = seatAvailable;
      styles[0]['seat']['unavailable'] = seatUnavailable;
    }

    this.viewerService.setStyles(styles);
  }

  public setupNeighborSectionNodes(availableSectionIds: string[]): void {
    if (this.demoDataService.isDemoActive()) {
      return;
    }

    const nhNodes = this.viewerService.getNodesByType('section_nh');
    if (!nhNodes.length) {
      return;
    }

    const nhAvailable = nhNodes
      .filter(node => availableSectionIds.includes(node.id))
      .map(node => node.id);

    this.viewerService.setAvailability('section_nh', nhAvailable);
  }

  private setBaseAvailability(availability: string[]): void {
    this.baseAvailability.push(...availability);
  }

  private checkIsTopView(mapId: string): boolean {
    const isTopView = mapId === this.mapViewerLoadOptions.map_id;
    this._isTopView.set(isTopView);
    return isTopView;
  }

  private updateSectionIdTranslation() {
    if (this._isTopView()) {
      const sections = this.viewerService.getNodesByType('section');
      const gaSections = this.viewerService.getNodesByType('general_admission');
      const mergedSections = [...sections, ...gaSections];

      for (const section of mergedSections) {
        if (!this.sectionsMmcIdToTdc[section.original_id]) {
          this.sectionsMmcIdToTdc[section.original_id] = section.id;
        }
        if (!this.sectionsTdcToMmc[section.id]) {
          this.sectionsTdcToMmc[section.id] = section.original_id;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.viewerSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
